// === PRESETS D'ÉQUIPEMENT ===
// v0.38.15 — Permet de sauvegarder/charger une configuration d'équipement
// complète (équipements perso par membre + sac partagé + consommables).
// Stocké en localStorage, persiste entre les runs.
//
// La logique pure (snapshot/apply) ne dépend pas de Phaser et est testable.
// La couche UI (boutons "Sauver" / "Charger" dans RunPrepScene) appelle
// `applyPreset()` qui modifie InventoryManager + CampManager.

import { InventoryManager } from './InventoryManager';
import { CampManager } from './CampManager';
import { PersonalSlot, PERSONAL_SLOTS } from '../types/Item.types';

const STORAGE_KEY = 'deadtide_equipment_presets';
const PRESET_COUNT = 3;

/** Snapshot d'un slot pour 1 membre : juste le definitionId (pas l'uid). */
export interface PresetSlot {
  /** Slot perso → definitionId, ou undefined si slot vide. */
  weapon?: string;
  armor?: string;
  belt?: string;
  ring?: string;
  boots?: string;
}

/** Un preset = snapshot complet du loadout à un instant T. */
export interface EquipmentPreset {
  id: number;          // 0, 1, 2 (3 slots fixes)
  isEmpty: boolean;    // true si jamais sauvegardé
  /** Date de dernière sauvegarde (ISO string). */
  savedAt?: string;
  /** Équipement par position de membre dans selectedIds (0=captain, 1=2e, 2=3e). */
  members: PresetSlot[];
  /** Definition ID du sac partagé, null si aucun. */
  backpack: string | null;
  /** Liste de definitionIds des consommables embarqués. */
  consumables: string[];
}

/** Pris en compte par snapshotCurrent : capture l'état actuel d'un membre. */
function snapshotMemberSlots(inv: InventoryManager, memberId: string): PresetSlot {
  const equip = inv.getEquipment(memberId);
  const slot: PresetSlot = {};
  for (const s of PERSONAL_SLOTS) {
    const item = equip[s];
    if (item) slot[s] = item.definitionId;
  }
  return slot;
}

/** Construit un preset à partir de l'état courant : équipe sélectionnée + sac + consommables. */
export function snapshotCurrent(
  inv: InventoryManager,
  selectedMemberIds: string[],
  presetId: number,
): EquipmentPreset {
  const members: PresetSlot[] = selectedMemberIds.map(id => snapshotMemberSlots(inv, id));
  const bag = inv.getBackpack();
  const cons = inv.getConsumables().map(c => c.definitionId);
  return {
    id: presetId,
    isEmpty: false,
    savedAt: new Date().toISOString(),
    members,
    backpack: bag?.definitionId ?? null,
    consumables: cons,
  };
}

/**
 * Applique un preset : déséquipe tout, puis ré-équipe ce qui est trouvable
 * dans le coffre (matching par definitionId, pas par uid).
 *
 * Best effort : si un item du preset n'est plus dans le coffre, ce slot est
 * laissé vide (pas d'erreur). Renvoie un compte-rendu pour l'UI.
 *
 * Garde-fou (v0.38.17) : si déséquiper renverrait plus d'items que le coffre
 * ne peut en accueillir, on annule l'opération avant la moindre modification.
 * Sans ça, les items en surplus étaient silencieusement perdus.
 */
export interface ApplyResult {
  equipped: number;     // nombre de slots remplis
  missing: number;      // nombre de slots impossibles (item plus dispo)
  consumablesTaken: number;
  consumablesMissing: number;
  /** True si l'opération a été annulée (coffre trop petit pour accueillir
   *  l'équipement actuel). Aucune modification n'a alors été faite. */
  aborted?: boolean;
}

/** Compte combien d'items équipés/sac il faudrait remettre au coffre. */
function countItemsToReturn(inv: InventoryManager, selectedMemberIds: string[]): number {
  let n = 0;
  for (const memberId of selectedMemberIds) {
    const equip = inv.getEquipment(memberId);
    for (const slot of PERSONAL_SLOTS) {
      if (equip[slot]) n++;
    }
  }
  if (inv.getBackpack()) n++;
  return n;
}

export function applyPreset(
  inv: InventoryManager,
  camp: CampManager,
  preset: EquipmentPreset,
  selectedMemberIds: string[],
): ApplyResult {
  const result: ApplyResult = { equipped: 0, missing: 0, consumablesTaken: 0, consumablesMissing: 0 };

  // 0) Pré-validation : vérifier qu'il y a la place dans le coffre pour
  //    accueillir tout ce qui va être déséquipé. Sinon on annule.
  const itemsToReturn = countItemsToReturn(inv, selectedMemberIds);
  const free = camp.getStorageCapacity() - camp.getStorage().length;
  if (itemsToReturn > free) {
    result.aborted = true;
    return result;
  }

  // 1) Déséquiper tout l'équipement perso de tous les membres et le remettre au coffre
  for (const memberId of selectedMemberIds) {
    for (const slot of PERSONAL_SLOTS) {
      const removed = inv.unequipFromCharacter(memberId, slot);
      if (removed) {
        // storeItem ne peut plus échouer ici grâce à la pré-validation,
        // mais on assert quand même : si jamais, on re-équipe pour ne pas perdre.
        if (!camp.storeItem({ uid: removed.uid, definitionId: removed.definitionId })) {
          inv.equipItemOnCharacter(removed, memberId, slot);
        }
      }
    }
  }
  // 2) Déséquiper le sac partagé
  const oldBag = inv.unequipBackpack();
  if (oldBag) {
    if (!camp.storeItem({ uid: oldBag.uid, definitionId: oldBag.definitionId })) {
      inv.equipBackpack(oldBag);
    }
  }
  // 3) Renvoyer les consommables actuels au coffre (discard côté équipe + addConsumable côté camp)
  for (const c of [...inv.getConsumables()]) {
    inv.discardConsumable(c.uid);
    camp.addConsumable(c.definitionId);
  }

  // 4) Ré-équiper depuis le preset, en prenant les items dans le coffre
  for (let i = 0; i < preset.members.length && i < selectedMemberIds.length; i++) {
    const memberId = selectedMemberIds[i];
    const target = preset.members[i];
    for (const slot of PERSONAL_SLOTS) {
      const wantedDefId = target[slot];
      if (!wantedDefId) continue;
      // Trouver un item du coffre avec ce definitionId
      const found = camp.getStorage().find(it => it.definitionId === wantedDefId);
      if (!found) {
        result.missing++;
        continue;
      }
      const withdrawn = camp.withdrawItem(found.uid);
      if (!withdrawn) {
        result.missing++;
        continue;
      }
      const replaced = inv.equipItemOnCharacter(withdrawn, memberId, slot);
      if (replaced) {
        // Ce cas ne devrait pas arriver (on a tout déséquipé), mais on stocke
        // par sécurité plutôt que de perdre l'item.
        camp.storeItem({ uid: replaced.uid, definitionId: replaced.definitionId });
      }
      result.equipped++;
    }
  }

  // 5) Sac partagé
  if (preset.backpack) {
    const found = camp.getStorage().find(it => it.definitionId === preset.backpack);
    if (found) {
      const withdrawn = camp.withdrawItem(found.uid);
      if (withdrawn) {
        const replaced = inv.equipBackpack(withdrawn);
        if (replaced) camp.storeItem({ uid: replaced.uid, definitionId: replaced.definitionId });
        result.equipped++;
      } else {
        result.missing++;
      }
    } else {
      result.missing++;
    }
  }

  // 6) Consommables (camp.withdrawConsumable retire 1 instance correspondante)
  for (const defId of preset.consumables) {
    if (inv.getConsumables().length >= inv.getMaxConsumables()) {
      result.consumablesMissing++;
      continue;
    }
    const stored = camp.getConsumables().find(c => c.definitionId === defId);
    if (!stored) {
      result.consumablesMissing++;
      continue;
    }
    const withdrawn = camp.withdrawConsumable(stored.uid);
    if (withdrawn) {
      inv.addConsumable(withdrawn.definitionId);
      result.consumablesTaken++;
    }
  }

  return result;
}

// === PERSISTENCE ===
// 3 slots fixes en localStorage. Pas de migration : si la structure change,
// on lit avec fallback sur emptyPreset(id).

function emptyPreset(id: number): EquipmentPreset {
  return { id, isEmpty: true, members: [], backpack: null, consumables: [] };
}

/** Charge les 3 presets depuis localStorage. Retourne toujours 3 entrées. */
export function loadAllPresets(): EquipmentPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPresets();
    const parsed = JSON.parse(raw) as EquipmentPreset[];
    if (!Array.isArray(parsed)) return defaultPresets();
    return defaultPresets().map((empty, i) => parsed.find(p => p.id === i) ?? empty);
  } catch {
    return defaultPresets();
  }
}

/** Persiste les 3 presets en localStorage. */
export function saveAllPresets(presets: EquipmentPreset[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // localStorage plein → silently fail (les presets resteront en mémoire le temps de la scène)
  }
}

function defaultPresets(): EquipmentPreset[] {
  const out: EquipmentPreset[] = [];
  for (let i = 0; i < PRESET_COUNT; i++) out.push(emptyPreset(i));
  return out;
}

export const PRESET_SLOTS_COUNT = PRESET_COUNT;
